import { createPlanTabsSelectors, getSelectorAttribute } from '@weft/app-selectors';
import type { Currency, FixedProductInput, PromotionInput, UsageProductInput } from '@weft/types';
import React from 'react';
import { Counter } from '../../../../../../components/design/Counter';
import { ErrorIcon } from '../../../../../../components/icons/ErrorIcon';
import type { TabRecord } from '../../../../../../utils/types/tab-record';
import { FixedChargesTabContent } from '../create-plan-page-tabs/FixedChargesTabContent/FixedChargesTabContent';
import { PromotionChargesTabContent } from '../create-plan-page-tabs/PromotionChargesTabContent/PromotionChargesTabContent';
import { UsageChargesTabContent } from '../create-plan-page-tabs/UsageChargesTabContent/UsageChargesTabContent';
import { CREATE_PLAN_TABS } from '../create-plan-page-types/create-plan-tab';
import type { CreatePlanTab } from '../create-plan-page-types/create-plan-tab';

export type GetCreatePlanTabsPayload = {
  fixedProducts: FixedProductInput[];
  usageProducts: UsageProductInput[];
  promotions: PromotionInput[];
  currency: Currency;
  doesHaveError?: (tab: CreatePlanTab) => boolean;
};

export function getCreatePlanTabs({ fixedProducts, usageProducts, promotions, currency, doesHaveError }: GetCreatePlanTabsPayload): TabRecord<CreatePlanTab> {
  return {
    [CREATE_PLAN_TABS.serviceFees]: {
      id: 0,
      render: (
        <FixedChargesTabContent
          fixedProducts={fixedProducts}
          currency={currency}
        />
      ),
      label: (
        <div className="flex items-center space-x-2">
          <span>Service Fees</span>
          <Counter
            className="group-[.is-selected]:bg-[#CCD7FC] group-[.is-selected]:text-[#458FFF]"
            count={fixedProducts.length}
          />
          {doesHaveError?.(CREATE_PLAN_TABS.serviceFees) && (
            <ErrorIcon className="fill-[#FC9592] group-[.is-selected]:fill-[#FF154D]"/>
          )}
        </div>
      ),
      selectorAttribute: getSelectorAttribute(createPlanTabsSelectors.serviceFeesTab),
    },
    [CREATE_PLAN_TABS.tierAndVolumePricing]: {
      id: 1,
      render: (
        <UsageChargesTabContent
          usageProducts={usageProducts}
          currency={currency}
        />
      ),
      label: (
        <div className="flex items-center space-x-2">
          <span>Usage-based Fees</span>
          <Counter
            className="group-[.is-selected]:bg-[#CCD7FC] group-[.is-selected]:text-[#458FFF]"
            count={usageProducts.length}
          />
          {doesHaveError?.(CREATE_PLAN_TABS.tierAndVolumePricing) && (
            <ErrorIcon className="fill-[#FC9592] group-[.is-selected]:fill-[#FF154D]"/>
          )}
        </div>
      ),
      selectorAttribute: getSelectorAttribute(createPlanTabsSelectors.tierAndVolumePricingTab),
    },
    [CREATE_PLAN_TABS.promotions]: {
      id: 2,
      render: (
        <PromotionChargesTabContent
          promotions={promotions}
          currency={currency}
        />
      ),
      label: (
        <div className="flex items-center space-x-2">
          <span>Credits & Discounts</span>
          <Counter
            className="group-[.is-selected]:bg-[#CCD7FC] group-[.is-selected]:text-[#458FFF]"
            count={promotions.length}
          />
          {doesHaveError?.(CREATE_PLAN_TABS.promotions) && (
            <ErrorIcon className="fill-[#FC9592] group-[.is-selected]:fill-[#FF154D]"/>
          )}
        </div>
      ),
      selectorAttribute: getSelectorAttribute(createPlanTabsSelectors.promotionsTab),
    },
  };
}